import { CategoryDialog } from "@/components/categories/category-dialog";
import { ConfirmDeleteDialog } from "@/components/dialogs/ConfirmDeleteDialog";
import { UnifiedListLayout } from "@/components/layout/unified-list-layout";
import { IntroCard } from "@/components/shared/IntroCard";
import { UnifiedSearch } from "@/components/shared/UnifiedSearch";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useCategoriesQuery, useDeleteCategoryMutation } from '@/hooks/useCategoryQueries';
import { cn } from "@/lib/utils";
import { useProjectEntities } from '@/services/query/entities-queries';
import { TemplateCategory } from "@/services/supabase/templates";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    Book,
    BookOpen,
    Box,
    Briefcase,
    Calendar,
    Crown,
    Database,
    Feather,
    FileText,
    Flag,
    Ghost,
    Globe,
    Hammer,
    Heart,
    Image,
    Key,
    Layers,
    LayoutGrid,
    Lightbulb,
    Lock,
    Map,
    MapPin,
    MessageSquare,
    MoreHorizontal,
    Music,
    PawPrint,
    Pencil,
    Plus,
    Scroll,
    Shield,
    Skull,
    Star,
    Sword,
    Tent,
    Trash2,
    User,
    Users,
    Users2,
    Zap
} from "lucide-react";
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Icon Mapping
const ICON_MAP: Record<string, any> = {
    User, Users, Users2, MapPin, Map, Box, PawPrint, Calendar, BookOpen, Layers,
    Sword, Shield, Crown, Scroll, Feather, Skull, Ghost, Tent, Flag,
    Globe, Key, Lock, Briefcase, Hammer, Image, Music, Heart, Star,
    Zap, Lightbulb, MessageSquare, Book, FileText
};

const getCategoryIcon = (category: TemplateCategory) => {
    // If icon is stored in DB
    if (category.icon && ICON_MAP[category.icon]) {
        return ICON_MAP[category.icon];
    }

    // Fallback based on name
    const lower = category.name.toLowerCase();
    if (lower.includes('character') || lower.includes('person')) return User;
    if (lower.includes('location') || lower.includes('place')) return MapPin;
    if (lower.includes('item') || lower.includes('object')) return Box;
    if (lower.includes('creature') || lower.includes('beast')) return PawPrint;
    if (lower.includes('group') || lower.includes('faction')) return Users;
    if (lower.includes('event')) return Calendar;
    if (lower.includes('lore') || lower.includes('history')) return BookOpen;
    return Layers; // Default
};

export default function ElementsOverviewPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [hoveredId, setHoveredId] = useState<number | null>(null);

    // Dialog State
    const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
    const [categoryDialogMode, setCategoryDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | null>(null);

    // Delete Dialog State
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<TemplateCategory | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isIntroVisible, setIsIntroVisible] = useState(true);

    useEffect(() => {
        if (projectId) {
            const isClosed = localStorage.getItem(`elements_intro_closed_${projectId}`);
            setIsIntroVisible(isClosed !== 'true');
        }
    }, [projectId]);

    const dismissIntro = () => {
        if (projectId) {
            localStorage.setItem(`elements_intro_closed_${projectId}`, 'true');
            setIsIntroVisible(false);
        }
    };

    // Data fetching
    const { data: categories = [], isLoading: categoriesLoading } = useCategoriesQuery(parseInt(projectId || '0'));

    // Filter categories
    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const { data: allEntities = [], isLoading: entitiesLoading } = useProjectEntities(projectId);
    const deleteCategoryMutation = useDeleteCategoryMutation();

    // Handlers
    const getEntityCount = (categoryId: number) => {
        return allEntities.filter(e => e.category_id === categoryId).length;
    };

    const handleCreateCategory = () => {
        setCategoryDialogMode('create');
        setSelectedCategory(null);
        setIsCategoryDialogOpen(true);
    };

    const handleEditCategory = (e: React.MouseEvent, category: any) => {
        e.stopPropagation();
        setCategoryDialogMode('edit');
        setSelectedCategory(category);
        setIsCategoryDialogOpen(true);
    };

    const handleDeleteClick = (e: React.MouseEvent, category: any) => {
        e.stopPropagation();
        setCategoryToDelete(category);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async (confirmed: boolean) => {
        if (!confirmed || !categoryToDelete || !projectId) {
            setIsDeleteDialogOpen(false);
            return;
        }

        try {
            await deleteCategoryMutation.mutateAsync({
                categoryId: categoryToDelete.id,
                projectId: parseInt(projectId)
            });
            setIsDeleteDialogOpen(false);
            setCategoryToDelete(null);
        } catch (error) {
            console.error("Failed to delete category", error);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20, filter: "blur(10px)" },
        visible: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: { duration: 0.5 }
        }
    };

    if (categoriesLoading || entitiesLoading) {
        return (
            <UnifiedListLayout>
                <div className="space-y-12">
                    <div className="space-y-4">
                        <Skeleton className="h-12 w-64 bg-primary/5" />
                        <Skeleton className="h-6 w-96 bg-primary/5" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <Skeleton key={i} className="h-[280px] w-full rounded-3xl bg-primary/5" />
                        ))}
                    </div>
                </div>
            </UnifiedListLayout>
        );
    }

    return (
        <UnifiedListLayout
            toolbar={
                <>
                    <UnifiedSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search elements..."
                        className="w-full max-w-sm"
                    />
                    <Button onClick={handleCreateCategory} className="shadow-lg shadow-primary/20">
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Element
                    </Button>
                </>
            }
        >
            {isIntroVisible && (
                <IntroCard
                    title="World Building & Story Bible"
                    description="Elements are the building blocks of your world. Creates characters, locations, items, and more to build a comprehensive story bible that keeps your narrative consistent."
                    onClose={dismissIntro}
                    className="mb-8"
                    actions={
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Quick Actions</h4>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    Start by defining the core pillars of your story.
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                className="w-full justify-start text-xs h-9"
                                onClick={handleCreateCategory}
                            >
                                <Plus className="h-3 w-3 mr-2" />
                                Create New Element
                            </Button>
                        </div>
                    }
                />
            )}

            {/* Categories Grid */}
            <AnimatePresence mode="wait">
                {filteredCategories.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex flex-col items-center justify-center py-32 px-4 text-center border border-dashed border-border/30 rounded-3xl bg-muted/5"
                    >
                        <div className="h-20 w-20 rounded-full bg-muted/10 flex items-center justify-center mb-8 border border-white/5 shadow-inner">
                            <LayoutGrid className="h-10 w-10 text-muted-foreground/60" />
                        </div>
                        <h3 className="text-2xl font-semibold mb-3 tracking-tight">No Elements Created</h3>
                        <p className="text-muted-foreground mb-8 max-w-md mx-auto text-lg leading-relaxed">
                            Start defining your world by creating new elements like characters, locations, or items.
                        </p>
                        <Button onClick={handleCreateCategory} size="lg" className="shadow-xl shadow-primary/20">
                            <Plus className="mr-2 h-5 w-5" />
                            Create First Element
                        </Button>
                    </motion.div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
                    >
                        {filteredCategories.map((category) => {
                            const Icon = getCategoryIcon(category);
                            const count = getEntityCount(category.id);

                            return (
                                <motion.div
                                    key={category.id}
                                    variants={itemVariants}
                                    onMouseEnter={() => setHoveredId(category.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                    onClick={() => navigate(`/projects/${projectId}/elements/${category.id}`)}
                                    className="relative group cursor-pointer"
                                >
                                    <div className={cn(
                                        "relative h-[240px] rounded-3xl overflow-hidden transition-all duration-500",
                                        "border border-border/40 bg-card/30 backdrop-blur-sm",
                                        "hover:bg-card/50 hover:border-primary/20 hover:shadow-2xl hover:shadow-primary/5",
                                        "flex flex-col justify-between p-8"
                                    )}>
                                        {/* Subtle Gradient Background */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                        {/* Watermark Icon with Smooth Animation */}
                                        <motion.div
                                            className="absolute -right-6 -bottom-6 text-foreground origin-bottom-right pointer-events-none"
                                            initial={{ rotate: 12, scale: 1.5, opacity: 0.03 }}
                                            animate={{
                                                rotate: hoveredId === category.id ? 0 : 12,
                                                scale: hoveredId === category.id ? 1.2 : 1.5,
                                                x: hoveredId === category.id ? 10 : 0,
                                                y: hoveredId === category.id ? -10 : 0,
                                                opacity: hoveredId === category.id ? 0.07 : 0.03
                                            }}
                                            transition={{ duration: 0.3, ease: "easeOut" }}
                                        >
                                            <Icon className="h-48 w-48" />
                                        </motion.div>

                                        {/* Header */}
                                        <div className="relative z-10 flex justify-between items-start">
                                            <div className={cn(
                                                "h-14 w-14 rounded-2xl flex items-center justify-center transition-all duration-300",
                                                "bg-muted/10 border border-white/5",
                                                "group-hover:bg-primary/10 group-hover:text-primary group-hover:scale-110"
                                            )}>
                                                <Icon className="h-7 w-7 text-muted-foreground group-hover:text-primary transition-colors" />
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {/* Actions Menu */}
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground hover:bg-background/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-40 border-border/50 bg-background/95 backdrop-blur-md">
                                                        <DropdownMenuItem onClick={(e) => handleEditCategory(e, category)}>
                                                            <Pencil className="mr-2 h-4 w-4" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={(e) => handleDeleteClick(e, category)}
                                                            className="text-destructive focus:text-destructive"
                                                        >
                                                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>

                                                <div className={cn(
                                                    "h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0",
                                                    "bg-primary/10 text-primary"
                                                )}>
                                                    <ArrowRight className="h-4 w-4" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="relative z-10 space-y-3">
                                            <div>
                                                <h3 className="text-xl font-bold tracking-tight text-foreground/90 group-hover:text-foreground transition-colors">
                                                    {category.name}
                                                </h3>
                                                <p className="text-sm text-muted-foreground line-clamp-2 mt-1.5 leading-relaxed">
                                                    {category.description || `Manage ${category.name.toLowerCase()} in your story.`}
                                                </p>
                                            </div>

                                            <div className="pt-4 flex items-center gap-2">
                                                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/50 border border-border/50 text-xs font-medium text-muted-foreground group-hover:bg-background/80 transition-colors">
                                                    <Database className="h-3 w-3 opacity-70" />
                                                    {count} {count === 1 ? 'Entity' : 'Entities'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Category Dialog */}
            <CategoryDialog
                isOpen={isCategoryDialogOpen}
                onClose={() => setIsCategoryDialogOpen(false)}
                mode={categoryDialogMode}
                category={selectedCategory || undefined}
                projectId={parseInt(projectId || '0')}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                {isDeleteDialogOpen && categoryToDelete && (
                    <ConfirmDeleteDialog
                        onClose={() => setIsDeleteDialogOpen(false)}
                        data={{
                            itemType: 'element',
                            itemName: categoryToDelete.name
                        }}
                        onSave={handleConfirmDelete}
                    />
                )}
            </Dialog>
        </UnifiedListLayout>
    );
}
